import React from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  Link,
} from "@mui/material";
import { FormProvider } from "react-hook-form";
import { ChevronBigLeft, InfoInfo } from "@tiny/icons";
import useSelectRules from "./SelectRules.hooks";
import RuleTypeSelector from "./RuleTypeSelector";
import ShoppingActivityFields from "./ShoppingActivityFields";
import AssignedAudienceFields from "./AssignedAudienceFields";
import MarketingActivityFields from "./MarketingActivityFields";
import { FORM_CONSTANTS, API_DOCS_LINKS } from "./SelectRules.constants";

const SelectRules = ({
  id,
  triggerType = "RULE",
}: {
  id: string;
  triggerType?: string;
}) => {
  const {
    methods,
    formValues,
    goBack,
    onSubmit,
    viewMode,
    hideDrawer,
    saveRulesDisabled,
    audiences,
    handleAudienceSearch,
    loadMoreAudiences,
    isLoadingMore,
    hasMoreResults,
    isLoading,
    isTinyEmail,
    activities,
    handleActivitySearch,
    loadMoreActivities,
    isLoadingMoreActivities,
    hasMoreActivityResults,
    isActivityLoading,
  } = useSelectRules({ id });

  const handleCancel = () => {
    viewMode ? hideDrawer() : goBack();
  };

  const isFormValid = () => {
    if (formValues.ruleType === FORM_CONSTANTS.RULE_TYPES.SHOPPING_ACTIVITY) {
      return !!formValues.event;
    }
    if (formValues.ruleType === FORM_CONSTANTS.RULE_TYPES.ASSIGNED_AUDIENCE) {
      return formValues.audiences && formValues.audiences.length > 0;
    }
    if (formValues.ruleType === FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY) {
      return !!formValues.activityId;
    }
    if (formValues.ruleType === FORM_CONSTANTS.RULE_TYPES.CLEANED_CUSTOMER) {
      return formValues.audiences && formValues.audiences.length > 0;
    }
    return false;
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} style={{ height: "100%" }}>
        <Box
          sx={{
            py: 4,
            px: 6,
            display: "flex",
            flexDirection: "column",
            width: { xs: "320px", xl: "20vw" },
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton sx={{ px: 0 }} onClick={handleCancel}>
              <ChevronBigLeft color="success" />
            </IconButton>
            <Typography variant="h2">
              {triggerType === "RULE" ? "Rules" : "API"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 5,
              mt: 6,
              flex: 1,
            }}
          >
            <RuleTypeSelector triggerType={triggerType} />

            {formValues.ruleType ===
              FORM_CONSTANTS.RULE_TYPES.SHOPPING_ACTIVITY && (
              <ShoppingActivityFields formValues={formValues} />
            )}

            {(formValues.ruleType ===
              FORM_CONSTANTS.RULE_TYPES.ASSIGNED_AUDIENCE ||
              formValues.ruleType ===
                FORM_CONSTANTS.RULE_TYPES.CLEANED_CUSTOMER) && (
              <AssignedAudienceFields
                audiences={audiences}
                handleAudienceSearch={handleAudienceSearch}
                loadMoreAudiences={loadMoreAudiences}
                isLoadingMore={isLoadingMore}
                hasMoreResults={hasMoreResults}
                isLoading={isLoading}
                isTinyEmail={isTinyEmail}
              />
            )}

            {formValues.ruleType ===
              FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY && (
              <MarketingActivityFields
                formValues={formValues}
                activities={activities}
                handleActivitySearch={handleActivitySearch}
                loadMoreActivities={loadMoreActivities}
                isLoadingMore={isLoadingMoreActivities}
                hasMoreResults={hasMoreActivityResults}
                isLoading={isActivityLoading}
                isTinyEmail={isTinyEmail}
              />
            )}
            {triggerType === "API" && (
              <Alert
                severity="info"
                variant="outlined"
                iconMapping={{
                  info: (
                    <InfoInfo
                      sx={(theme) => ({
                        color: theme.palette.primary[100],
                      })}
                    />
                  ),
                }}
              >
                <Typography variant="p3">
                  Please see the API documentation on how to trigger a workflow.
                  <Link
                    href={
                      API_DOCS_LINKS[formValues.ruleType || ""] ||
                      API_DOCS_LINKS[
                        FORM_CONSTANTS.RULE_TYPES.ASSIGNED_AUDIENCE
                      ]
                    }
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontWeight: 500,
                      ml: 0.5,
                    }}
                  >
                    View Docs
                  </Link>
                  <br />
                  <b>
                    Note:- API Keys are only available to Enterprise customers.
                  </b>
                </Typography>
              </Alert>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!isFormValid() || saveRulesDisabled || viewMode}
            >
              Save
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default SelectRules;
